/**
 * Categorias e subcategorias exclusivas de Estabelecimentos (independentes das notícias).
 * Chaves localStorage distintas de `naviex-admin-news-*`.
 */

import {
  getEstabelecimentos,
  mergeEstabelecimentoSubcategoryIds,
  normalizeEstabelecimentoForStore,
  saveEstabelecimento,
} from './estabelecimentosStorage';
import { getNewsCategories, getNewsSubcategories } from './newsStorage';

const KEY_ESTABELECIMENTO_CATEGORIES = 'naviex-admin-estabelecimentos-categorias';
const KEY_ESTABELECIMENTO_SUBCATEGORIES = 'naviex-admin-estabelecimentos-subcategorias';

export type EstabelecimentoCategory = { id: string; name: string };
export type EstabelecimentoSubcategory = { id: string; categoryId: string; name: string };

/**
 * Alinhado ao seed DynamoDB (`iac/dynamodb-estabelecimentos-categorias-seed.tf`).
 * IDs das subs seguem `estsub-{slug(categoria)}-{slug(nome)}` (ver seed na AWS).
 * O formulário admin permite várias subcategorias por estabelecimento (`subcategoryIds`).
 */
const FALLBACK_CATEGORY_NAMES = ['Loja', 'Restaurante', 'Café', 'Bar'];
const FALLBACK_SUBCATEGORY_MAP: Record<string, string[]> = {
  Loja: [
    'Artesanato',
    'Cristais / Esotéricos',
    'Produtos naturais',
    'Produtos orgânicos',
    'Roupas / Moda',
    'Moda praia',
    'Acessórios / Bijuterias',
    'Souvenirs',
    'Decoração',
    'Equipamentos outdoor',
    'Camping / Trekking',
    'Empório local',
    'Doces / Produtos regionais',
    'Geral',
  ],
  Restaurante: [
    'Comida regional',
    'Brasileira',
    'Internacional',
    'Vegetariano / Vegano',
    'Orgânico / Natural',
    'Alta gastronomia',
    'Caseiro / Familiar',
    'Self-service',
    'À la carte',
    'Rodízio',
    'Buffet',
    'Temático',
    'Geral',
  ],
  Café: [
    'Café especial',
    'Cafeteria artesanal',
    'Brunch',
    'Doceria',
    'Padaria / Bakery',
    'Café colonial',
    'Vegano',
    'Café + coworking',
    'Geral',
  ],
  Bar: [
    'Música ao vivo',
    'Cervejaria artesanal',
    'Drinks / Coquetelaria',
    'Pub',
    'Bar noturno',
    'Bar alternativo',
    'Bar/restaurante',
    'Lounge',
    'Rooftop',
    'Geral',
  ],
};

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function normalizeText(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function defaultSeeds(): { cats: EstabelecimentoCategory[]; subs: EstabelecimentoSubcategory[] } {
  const cats: EstabelecimentoCategory[] = FALLBACK_CATEGORY_NAMES.map((name) => ({
    id: `estcat-${slugify(name)}`,
    name,
  }));
  const subs: EstabelecimentoSubcategory[] = [];
  cats.forEach((category) => {
    const subNames = FALLBACK_SUBCATEGORY_MAP[category.name] ?? ['Geral'];
    subNames.forEach((subName) => {
      subs.push({
        id: `estsub-${slugify(category.name)}-${slugify(subName)}`,
        categoryId: category.id,
        name: subName,
      });
    });
  });
  return { cats, subs };
}

/** Uma vez: copia taxonomia das notícias para chaves de estabelecimento e remapeia IDs nos cadastros. */
function migrateFromNewsCategoriesIfNeeded(): void {
  const existing = loadJson<EstabelecimentoCategory[]>(KEY_ESTABELECIMENTO_CATEGORIES, []);
  if (existing.length > 0) return;

  const newsCats = getNewsCategories();
  const newsSubs = getNewsSubcategories();

  if (newsCats.length === 0) {
    const { cats, subs } = defaultSeeds();
    saveJson(KEY_ESTABELECIMENTO_CATEGORIES, cats);
    saveJson(KEY_ESTABELECIMENTO_SUBCATEGORIES, subs);
    return;
  }

  const catIdMap = new Map<string, string>();
  const newCats: EstabelecimentoCategory[] = newsCats.map((c) => {
    const newId = c.id.startsWith('cat-') ? c.id.replace(/^cat-/, 'estcat-') : `estcat-${c.id}`;
    catIdMap.set(c.id, newId);
    return { id: newId, name: c.name };
  });

  const subIdMap = new Map<string, string>();
  const newSubs: EstabelecimentoSubcategory[] = newsSubs.map((s) => {
    const newId = s.id.startsWith('sub-') ? s.id.replace(/^sub-/, 'estsub-') : `estsub-${s.id}`;
    subIdMap.set(s.id, newId);
    const parent = catIdMap.get(s.categoryId) ?? s.categoryId.replace(/^cat-/, 'estcat-');
    return { id: newId, categoryId: parent, name: s.name };
  });

  saveJson(KEY_ESTABELECIMENTO_CATEGORIES, newCats);
  saveJson(KEY_ESTABELECIMENTO_SUBCATEGORIES, newSubs);

  getEstabelecimentos().forEach((e) => {
    let cid = e.categoryId;
    if (cid && catIdMap.has(cid)) cid = catIdMap.get(cid)!;
    const rawSubs = mergeEstabelecimentoSubcategoryIds(e);
    const newSubs = rawSubs.map((sid) => (sid && subIdMap.has(sid) ? subIdMap.get(sid)! : sid));
    const next = normalizeEstabelecimentoForStore({
      ...e,
      categoryId: cid,
      subcategoryIds: newSubs,
      subcategoryId: newSubs[0] ?? '',
    });
    if (
      cid !== e.categoryId ||
      JSON.stringify(newSubs) !== JSON.stringify(mergeEstabelecimentoSubcategoryIds(e))
    ) {
      saveEstabelecimento(next);
    }
  });
}

export function getEstabelecimentoCategories(): EstabelecimentoCategory[] {
  migrateFromNewsCategoriesIfNeeded();
  return loadJson<EstabelecimentoCategory[]>(KEY_ESTABELECIMENTO_CATEGORIES, []);
}

export function getEstabelecimentoSubcategories(): EstabelecimentoSubcategory[] {
  migrateFromNewsCategoriesIfNeeded();
  return loadJson<EstabelecimentoSubcategory[]>(KEY_ESTABELECIMENTO_SUBCATEGORIES, []);
}

export function getEstabelecimentoSubcategoriesByCategory(categoryId: string): EstabelecimentoSubcategory[] {
  if (!categoryId) return [];
  return getEstabelecimentoSubcategories().filter((s) => s.categoryId === categoryId);
}

export function addEstabelecimentoCategory(name: string): EstabelecimentoCategory {
  const normalized = name.trim();
  const list = getEstabelecimentoCategories();
  const exists = list.some((item) => normalizeText(item.name) === normalizeText(normalized));
  if (exists) throw new Error('Esta categoria já existe.');
  const next: EstabelecimentoCategory = {
    id: `estcat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: normalized,
  };
  saveJson(KEY_ESTABELECIMENTO_CATEGORIES, [...list, next]);
  return next;
}

export function addEstabelecimentoSubcategory(categoryId: string, name: string): EstabelecimentoSubcategory {
  const normalized = name.trim();
  const subs = getEstabelecimentoSubcategories();
  const exists = subs.some(
    (item) => item.categoryId === categoryId && normalizeText(item.name) === normalizeText(normalized)
  );
  if (exists) throw new Error('Esta subcategoria já existe para a categoria selecionada.');
  const next: EstabelecimentoSubcategory = {
    id: `estsub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    categoryId,
    name: normalized,
  };
  saveJson(KEY_ESTABELECIMENTO_SUBCATEGORIES, [...subs, next]);
  return next;
}

export async function removeEstabelecimentoCategory(
  categoryId: string,
  options?: { newCategoryId: string; newSubcategoryId: string }
): Promise<{ ok: boolean; establishmentsCount: number }> {
  const establishments = getEstabelecimentos();
  const estCount = establishments.filter((e) => e.categoryId === categoryId).length;

  if (estCount > 0 && !options) {
    return { ok: false, establishmentsCount: estCount };
  }

  if (estCount > 0 && options) {
    establishments
      .filter((e) => e.categoryId === categoryId)
      .forEach((e) =>
        saveEstabelecimento(
          normalizeEstabelecimentoForStore({
            ...e,
            categoryId: options.newCategoryId,
            subcategoryIds: [options.newSubcategoryId],
            subcategoryId: options.newSubcategoryId,
          })
        )
      );
  }

  const cats = getEstabelecimentoCategories().filter((c) => c.id !== categoryId);
  const subs = getEstabelecimentoSubcategories().filter((s) => s.categoryId !== categoryId);
  saveJson(KEY_ESTABELECIMENTO_CATEGORIES, cats);
  saveJson(KEY_ESTABELECIMENTO_SUBCATEGORIES, subs);
  return { ok: true, establishmentsCount: 0 };
}

export async function removeEstabelecimentoSubcategory(
  subcategoryId: string,
  options?: { newCategoryId: string; newSubcategoryId: string }
): Promise<{ ok: boolean; establishmentsCount: number }> {
  const establishments = getEstabelecimentos();
  const estCount = establishments.filter((e) => mergeEstabelecimentoSubcategoryIds(e).includes(subcategoryId)).length;

  if (estCount > 0 && !options) {
    return { ok: false, establishmentsCount: estCount };
  }

  if (estCount > 0 && options) {
    establishments
      .filter((e) => mergeEstabelecimentoSubcategoryIds(e).includes(subcategoryId))
      .forEach((e) => {
        const nextIds = mergeEstabelecimentoSubcategoryIds(e)
          .map((id) => (id === subcategoryId ? options.newSubcategoryId : id))
          .filter(Boolean);
        const dedup = [...new Set(nextIds)];
        saveEstabelecimento(
          normalizeEstabelecimentoForStore({
            ...e,
            categoryId: options.newCategoryId,
            subcategoryIds: dedup,
            subcategoryId: dedup[0] ?? '',
          })
        );
      });
  }

  const subs = getEstabelecimentoSubcategories().filter((s) => s.id !== subcategoryId);
  saveJson(KEY_ESTABELECIMENTO_SUBCATEGORIES, subs);
  return { ok: true, establishmentsCount: 0 };
}
